import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  id: "mf-login",
  class: "o-page-wrap mx-auto min-h-[70rem] w-full max-w-[490px]"
}
const _hoisted_2 = { class: "ocm-headline2 mb-6 pt-8" }
const _hoisted_3 = {
  id: "country_desc_1",
  class: "mb-5 mt-6"
}
const _hoisted_4 = {
  key: 0,
  class: "mt-4 flex text-gray-70",
  "data-test-address-country-info": ""
}
const _hoisted_5 = { id: "country_desc_2" }
const _hoisted_6 = ["href"]
const _hoisted_7 = ["href"]

import SharedButton from "@SharedVueComponents/SharedButton.vue";
import SharedDropdown from "@SharedVueComponents/SharedDropdown.vue";
import SharedForm from "@SharedVueComponents/SharedForm/SharedForm.vue";
import SharedRadio from "@SharedVueComponents/SharedRadio/SharedRadio.vue";
import SharedRadioGroup from "@SharedVueComponents/SharedRadio/SharedRadioGroup.vue";
import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon.vue";
import { keepFooterOnBottom } from "@SharedVueComposables/use-footer";
import { contextPath } from "@ocm/services/ocm-object";
import { $axios } from "@ocm/axios";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { pushEvent } from "@/services/dataLayerLogin";
import { RegisterResponseData, RegisterSubmitError } from "@/types/register.type";
import OcmMessenger from "ocm_mercurius_messenger";
import { messengerPublicationTypes } from "@ocm/services/services.constants";
import { getDepartmentCookie } from "@ocm/services/departmentCookie";

const flagBaseUrl = "/_ui/responsive/common/images/flags/";

export default /*@__PURE__*/_defineComponent({
  __name: 'RegisterLastStepPage',
  setup(__props) {

const { t } = useI18n();

const confirmationPage = window.ocm?.config?.confirmationPage;

const salutation = ref<string | null>(null);
const backendErrorMessage = ref<string | undefined>(undefined);
const isLoading = ref<boolean>(false);
const isDeliveryCountry = computed(() => DEFAULT_COUNTRY.includes(country.value || ""));
const hasDepartmentCookieOnload = !!getDepartmentCookie();

const selectableCountryItems = transformSelectableCountries(confirmationPage?.selectableCountries);
const country = ref<string | undefined>(getPreselectedCountry());
const flagURLs = getFlagURLs(confirmationPage?.selectableCountries);

const DEFAULT_COUNTRY = ["DE", "AT", "CH"];

keepFooterOnBottom();

function getPreselectedCountry() {
	if (!confirmationPage?.selectableCountries) return;
	const preselectedCountry = confirmationPage?.preferredCountryIsoCode;
	if (!preselectedCountry || !selectableCountryItems[preselectedCountry]) {
		return confirmationPage.selectableCountries[0].isocode;
	}
	return preselectedCountry;
}

async function submitForm(event: Event) {
	isLoading.value = true;
	try {
		const form = getFormFromEvent(event);
		if (!form) return;

		if (!hasDepartmentCookieOnload) sendChangeDepartment(salutation.value || "mr");

		const response = await postFormData(form);
		handleSubmitResponse(response.data);
	} catch (err) {
		handleSubmitError(err as RegisterSubmitError);
	}
}

function getFormFromEvent(event: Event): HTMLFormElement | null {
	return (event.target as HTMLElement).closest("form");
}

async function postFormData(form: HTMLFormElement) {
	const requestUrl = confirmationPage?.formAction || "";

	return $axios.post(requestUrl, getEncodedFormData(form), {
		params: getUrlQueryParams(),
	});
}

function getEncodedFormData(form: HTMLFormElement): string {
	const formData = new FormData(form);
	const formEncoded = new URLSearchParams();

	formData.forEach((value, key) => {
		if (typeof value === "string") {
			formEncoded.append(key, value);
		}
	});

	return formEncoded.toString();
}

function getUrlQueryParams(): Record<string, string> {
	return Object.fromEntries(new URLSearchParams(window.location.search));
}

function handleSubmitResponse(data: RegisterResponseData) {
	if (data.httpStatus === 200) {
		window.location = data.redirectUrl;
	} else {
		handleError();
	}
}

function handleSubmitError(error: RegisterSubmitError): void {
	const errorMessages = error?.response?.data?.globalErrorMessages;
	handleError(errorMessages?.[0]);
}

function handleError(newErrorMessage?: string | undefined) {
	backendErrorMessage.value = newErrorMessage || t("lastStep.default_backend_error_message");
	trackBackendError(backendErrorMessage.value);
	isLoading.value = false;
}

function transformSelectableCountries(countries: SelectableCountry[] | undefined) {
	if (!countries) return {};
	return countries.reduce((acc: SelectableDropdownCountryItem, country: SelectableCountry) => {
		acc[country.isocode] = country.name;
		return acc;
	}, {});
}

function getFlagURLs(countries: SelectableCountry[] | undefined) {
	if (!countries) return [];
	return countries.map((country: SelectableCountry) => flagBaseUrl + country.flag, {});
}

watch(country, () => {
	trackCountryChange();
});

function handleSalutationChange(errorMessage?: string) {
	if (errorMessage) trackSalutationError();
}

function trackSubmit() {
	pushEvent("RegisterButton");
}

function trackCountryChange() {
	pushEvent("change_country");
}

function trackSalutationError() {
	pushEvent("submit_salutation_error");
}

function trackBackendError(errorMessage: string) {
	pushEvent("submit_backend_error", errorMessage);
}

const SALUTATION_TO_DEPARTMENT_MAP: Record<string, string> = {
	mr: "herren",
	mrs: "damen",
};

function sendChangeDepartment(salutation: string) {
	const department = SALUTATION_TO_DEPARTMENT_MAP[salutation] || "";
	OcmMessenger.publish(messengerPublicationTypes.DEPARTMENT_CHANGE, department);
}

return (_ctx: any,_cache: any) => {
  const _component_i18n_t = _resolveComponent("i18n-t")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t("lastStep.headline")), 1),
    _createVNode(SharedForm, {
      class: "ocm-body1 mb-15 mt-4",
      "no-submit-button-gap": "",
      novalidate: "novalidate",
      "error-message": backendErrorMessage.value || _ctx.$t('lastStep.required_error_message'),
      "show-error": !!backendErrorMessage.value ? true : undefined,
      onChange: _cache[3] || (_cache[3] = ($event: any) => (backendErrorMessage.value = undefined)),
      onSubmit: submitForm
    }, {
      submitButton: _withCtx(() => [
        _createVNode(SharedButton, {
          type: "submit",
          class: "w-full",
          "is-loading": isLoading.value,
          onClick: trackSubmit
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t("lastStep.submit")), 1)
          ]),
          _: 1
        }, 8, ["is-loading"])
      ]),
      default: _withCtx(() => [
        _createVNode(SharedRadioGroup, {
          modelValue: salutation.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((salutation).value = $event)),
          name: "gender",
          "display-name": _ctx.$t('lastStep.salutation_display_name'),
          label: `${_ctx.$t('lastStep.salutation_label')} ${_ctx.$t('lastStep.required_label')}`,
          "required-error-message": _ctx.$t('lastStep.salutation_error_message'),
          "inner-class": "mt-4 flex gap-4",
          required: "",
          onChangeErrorMsg: _cache[1] || (_cache[1] = (_, data) => handleSalutationChange(data.errorMessage))
        }, {
          default: _withCtx(() => [
            _createVNode(SharedRadio, { "radio-value": "mrs" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("lastStep.salutation_ms")), 1)
              ]),
              _: 1
            }),
            _createVNode(SharedRadio, { "radio-value": "mr" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("lastStep.salutation_mr")), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue", "display-name", "label", "required-error-message"]),
        _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t("lastStep.which_country")), 1),
        _createVNode(SharedDropdown, {
          modelValue: country.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((country).value = $event)),
          name: "originCountryCode",
          "display-name": _ctx.$t('lastStep.country_display_name'),
          placeholder: `${_ctx.$t('lastStep.country_placeholder')}`,
          label: `${_ctx.$t('lastStep.country_display_name')} ${_ctx.$t('lastStep.required_label')}`,
          options: _unref(selectableCountryItems),
          "image-urls": _unref(flagURLs),
          required: "",
          "aria-describedby": 'country_desc_1' + (!isDeliveryCountry.value ? ' country_desc_2' : '')
        }, null, 8, ["modelValue", "display-name", "placeholder", "label", "options", "image-urls", "aria-describedby"]),
        (!isDeliveryCountry.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(SvgIcon, {
                class: "mr-2 h-5 w-5 flex-none p-[1px]",
                "icon-name": "info"
              }),
              _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t("lastStep.shipping_only_in_germany")), 1)
            ]))
          : _createCommentVNode("", true),
        _createVNode(_component_i18n_t, {
          keypath: "lastStep.legal",
          tag: "p",
          class: "ocm-subtext1 my-6"
        }, {
          terms: _withCtx(() => [
            _createElementVNode("a", {
              href: `${_unref(contextPath)}/agb/`,
              class: "ocm-subtext2 underline"
            }, _toDisplayString(_ctx.$t("lastStep.terms")), 9, _hoisted_6)
          ]),
          privacyPolicy: _withCtx(() => [
            _createElementVNode("a", {
              href: `${_unref(contextPath)}/datenschutz/`,
              class: "ocm-subtext2 underline"
            }, _toDisplayString(_ctx.$t("lastStep.privacy_policy")), 9, _hoisted_7)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["error-message", "show-error"])
  ]))
}
}

})